import React, { useState } from 'react';
import { Link2, Copy, Check, ExternalLink, Sparkles } from 'lucide-react';
import { ModulePlaceholder } from '@/components/modules/common/ModulePlaceholder';
import { usePartnerOverview } from '@/hooks/usePartnerOverview';

export const ReferralsView: React.FC = () => {
  const { data } = usePartnerOverview();
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const handleCopy = (url: string, id: string) => {
    navigator.clipboard.writeText(url);
    setCopiedSlug(id);
    setTimeout(() => setCopiedSlug(null), 2000);
  };

  const referralLinks = [
    {
      id: 'default',
      name: 'Default Storefront Link',
      code: data.referralCode,
      url: data.referralUrl,
      destination: 'https://conversora.io',
      clicks: data.totalClicks,
      conversions: data.totalLeads,
      status: 'Active',
    },
  ];

  return (
    <ModulePlaceholder
      title="Referral Links & Attribution"
      subtitle="Share your personalized referral links and track merchant clicks and conversions"
      icon={Link2}
      badge="Active Tracking"
    >
      <div className="space-y-6">
        {/* Active Primary Referral Link Card */}
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-zinc-800">
            <div>
              <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider block">
                Primary Partner Link
              </span>
              <p className="text-sm text-zinc-300 mt-1">
                Share this link on your YouTube channel, social media, agency website, or direct WhatsApp chats.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-medium">
                Active & Tracked
              </span>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 font-mono text-xs text-indigo-300 select-all">
              {data.referralUrl}
            </div>
            <button
              type="button"
              onClick={() => handleCopy(data.referralUrl, 'primary')}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition cursor-pointer shadow-xs"
            >
              {copiedSlug === 'primary' ? (
                <>
                  <Check className="w-4 h-4" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy Link</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Links Table */}
        <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Your Tracking Links</h2>
            <span className="text-xs text-zinc-400">60-Day Cookie Window</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-zinc-900/60 border-b border-zinc-800 text-zinc-400 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Link Name</th>
                  <th className="py-3 px-4">Referral URL</th>
                  <th className="py-3 px-4">Destination</th>
                  <th className="py-3 px-4 text-right">Clicks</th>
                  <th className="py-3 px-4 text-right">Conversions</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {referralLinks.map((link) => (
                  <tr key={link.id} className="hover:bg-zinc-900/40 transition">
                    <td className="py-3.5 px-4 font-medium text-white">{link.name}</td>
                    <td className="py-3.5 px-4 font-mono text-indigo-400">
                      {link.url}
                    </td>
                    <td className="py-3.5 px-4 text-zinc-400 truncate max-w-xs">{link.destination}</td>
                    <td className="py-3.5 px-4 text-right font-mono text-zinc-200">
                      {link.clicks.toLocaleString()}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-emerald-400 font-semibold">
                      {link.conversions}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleCopy(link.url, link.id)}
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition cursor-pointer"
                          title="Copy Link"
                        >
                          {copiedSlug === link.id ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition"
                          title="Open Link"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Attribution Rules Notice */}
        <div className="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
            <span className="text-zinc-300">
              Cookie persistence ensures you get credited if a merchant visits through your link and signs up within 60 days.
            </span>
          </div>
        </div>
      </div>
    </ModulePlaceholder>
  );
};

export default ReferralsView;
